import { InputNumber } from 'antd';
import type { ComponentProps, ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  isRefusedNumericKey,
  selectionContext,
  stripToNumericText,
} from '@/lib/validation/numeric-input';

import { FormField } from '../form-field';

import styles from './number-field.module.scss';

type AntdInputNumberProps = Omit<
  ComponentProps<typeof InputNumber>,
  'value' | 'onChange' | 'defaultValue' | 'prefix' | 'suffix' | 'size'
>;

export type NumberFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = AntdInputNumberProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: ReactNode;
  help?: string;
  required?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  fieldSize?: 'default' | 'tall';
  /** Caps the integer-digit count as the admin types, auto-trimming excess digits rather than
   *  blocking Save or letting an unbounded value reach the server (ADO 113438). Decimal digits
   *  are unaffected — pair with `precision` for those. */
  maxIntegerDigits?: number;
};

/**
 * Strips a typed value down to `maxDigits` integer digits and, where `precision` is set, to that
 * many decimal digits — by **truncation**, never rounding.
 *
 * Trimming the decimals here rather than leaving them to antd is the whole fix for ADO 113438's
 * reopen: antd applies `precision` *after* the parser, so a decimal that carries
 * (`9999999999.9999` → `10000000000.00`) produced an eleventh integer digit that the cap had
 * already run past — and that value reached the form state, not just the display. Truncating
 * cannot carry, so the integer cap is the last word on length.
 */
function capDigits(raw: string, maxDigits: number, precision?: number): string {
  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const [intPart = '', ...rest] = unsigned.split('.');
  const capped = intPart.slice(0, maxDigits);
  const sign = negative ? '-' : '';
  if (rest.length === 0) return `${sign}${capped}`;

  const decimals = rest.join('.');
  // `precision === 0` means "no decimals at all", so check for undefined rather than falsiness.
  const trimmed = precision === undefined ? decimals : decimals.slice(0, precision);
  return trimmed ? `${sign}${capped}.${trimmed}` : `${sign}${capped}`;
}

export function NumberField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  label,
  help,
  required,
  prefix,
  suffix,
  fieldSize = 'default',
  className,
  maxIntegerDigits,
  onKeyDown,
  onPaste,
  ...rest
}: NumberFieldProps<TFieldValues, TName>) {
  // AC 102085: a non-digit is stripped as it is typed and never displayed (UAT 120963). `precision`
  // and `min` already say whether a `.` or a `-` can ever be part of this field's value.
  const allowed = {
    decimal: rest.precision !== 0,
    negative: rest.min === undefined || Number(rest.min) < 0,
  };
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <InputNumber
          id={name}
          size={fieldSize === 'tall' ? 'large' : 'middle'}
          status={fieldState.error ? 'error' : undefined}
          controls={false}
          prefix={prefix}
          suffix={suffix}
          className={[styles.number, className].filter(Boolean).join(' ')}
          // Strips whatever a paste carried that this field can never hold (AC 102085) — rc re-formats
          // the display from the parsed value on paste, so `12.34abc` shows `12.34`. A caller's own
          // `parser` in `rest` replaces this one.
          parser={(displayValue) => {
            const clean = stripToNumericText(displayValue ?? '', allowed);
            return maxIntegerDigits
              ? capDigits(clean, maxIntegerDigits, rest.precision as number)
              : clean;
          }}
          {...rest}
          // Not rc-input-number's own prop, so it reaches the inner `<input>` through its rest-prop
          // spread — the only place a typed character can be refused before it is displayed (see
          // `numeric-input.ts`). A caller's own guard runs first and wins if it `preventDefault`s.
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented) return;
            if (isRefusedNumericKey(event, allowed, selectionContext(event.currentTarget))) {
              event.preventDefault();
            }
          }}
          // The `parser` strips a paste that still carries a number, but rc keeps the raw text on
          // screen when nothing parseable is left — so a paste with no numeric residue at all
          // (`abc`) is refused here instead (AC 102085). A caller's own `onPaste` runs first and wins.
          onPaste={(event) => {
            onPaste?.(event);
            if (event.defaultPrevented) return;
            const { before, after } = selectionContext(event.currentTarget);
            const text = `${before}${event.clipboardData.getData('text')}${after}`;
            if (stripToNumericText(text, allowed) === '') event.preventDefault();
          }}
          value={field.value ?? null}
          onChange={field.onChange}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}
